import { Box, Button, Icon, Text, VStack } from '@chakra-ui/react';
import { Check } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { colors } from 'styles/colors';
import { B2B_REGISTRATION_INTL } from 'translations/b2bRegistrationIntl';
import { useLanguageContext } from 'context/LanguageContext/LanguageContext';
import { FormShell } from '../FormShell/FormShell';

export type B2bRegistrationSuccessViewProps = {
  onNavigateHome: () => void;
};

const B2B_SUCCESS_MARK_BORDER = '3px solid #2E8B57';
const B2B_SUCCESS_MARK_INSET = '5.33333206176758px';
const B2B_SUCCESS_MARK_SIZE = '53.33333206176758px';
/** Frame = inset × 2 + circle outer diameter (Figma layout). */
const B2B_SUCCESS_MARK_FRAME = '63.999996185302734px';

/**
 * Post-submit confirmation — layout and copy aligned with B2B success design (centered card, green mark, single CTA).
 */
export const B2bRegistrationSuccessView = ({ onNavigateHome }: B2bRegistrationSuccessViewProps) => {
  const { language } = useLanguageContext();
  const formDir = language === 'en' ? 'ltr' : 'rtl';
  const formLang = language === 'en' ? 'en' : 'ar';

  return (
    <FormShell
      bg={colors.b2b.footerBg}
      minH='100vh'
      innerMaxW={{ base: 'full', lg: '30rem' }}
      justifyContent='center'
      display='flex'
      flexDirection='column'
      cardProps={{
        px: { base: 6, lg: 10 },
        py: { base: 10, lg: 14 },
        boxShadow: 'none',
        borderColor: '#E0E0E0',
      }}
    >
      <VStack
        spacing={{ base: 5, lg: 6 }}
        align='center'
        textAlign='center'
        w='full'
        dir={formDir}
        lang={formLang}
      >
        <Box
          aria-hidden
          position='relative'
          w={B2B_SUCCESS_MARK_FRAME}
          h={B2B_SUCCESS_MARK_FRAME}
          flexShrink={0}
        >
          <Box
            position='absolute'
            top={B2B_SUCCESS_MARK_INSET}
            insetStart={B2B_SUCCESS_MARK_INSET}
            w={B2B_SUCCESS_MARK_SIZE}
            h={B2B_SUCCESS_MARK_SIZE}
            border={B2B_SUCCESS_MARK_BORDER}
            borderRadius='full'
            boxSizing='border-box'
            opacity={1}
            display='flex'
            alignItems='center'
            justifyContent='center'
            bg={colors.white}
          >
            <Icon as={Check} boxSize='26px' color='#2E8B57' strokeWidth={2.5} aria-hidden />
          </Box>
        </Box>

        <Text
          as='h1'
          fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
          fontWeight='700'
          fontSize={{ base: '1.375rem', lg: '1.5rem' }}
          lineHeight={1.3}
          color='dark.D800'
          letterSpacing='0'
        >
          <FormattedMessage {...B2B_REGISTRATION_INTL.success.title} />
        </Text>

        <Text
          fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
          fontWeight='400'
          fontSize='md'
          lineHeight={1.6}
          color='#6B757D'
          maxW='100%'
          whiteSpace='pre-line'
        >
          <FormattedMessage {...B2B_REGISTRATION_INTL.success.description} />
        </Text>

        <Box pt={2} w='full' display='flex' justifyContent='center'>
          <Button
            type='button'
            variant='b2bPrimaryLarge'
            onClick={onNavigateHome}
            w='190px'
            maxW='100%'
            minW='50.64px'
            h='64px'
            minH='64px'
            px='16px'
            py='8px'
            gap='8px'
            borderRadius='16px'
            display='inline-flex'
            alignItems='center'
            justifyContent='center'
          >
            <FormattedMessage
              id='b2b.registration.success.backHome'
              defaultMessage='Back to home'
            />
          </Button>
        </Box>
      </VStack>
    </FormShell>
  );
};
